import React, { forwardRef } from 'react'
import styled, { keyframes, css } from 'styled-components'

const dropletIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(15px) scale(0.6, 0.3);
    border-radius: 40% 40% 4px 4px;
  }
  35% {
    opacity: 0.8;
    transform: translateY(-4px) scale(1.03, 0.97);
    border-radius: 15% 15% 4px 4px;
  }
  70% {
    transform: translateY(2px) scale(0.99, 1.01);
    border-radius: 8% 8% 4px 4px;
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1, 1);
    border-radius: 4px;
  }
`

const dropletOut = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) scale(1, 1);
    border-radius: 4px;
  }
  30% {
    opacity: 0.9;
    transform: translateY(2px) scale(0.97, 1.02);
    border-radius: 8% 8% 4px 4px;
  }
  100% {
    opacity: 0;
    transform: translateY(20px) scale(0.5, 0.3);
    border-radius: 40% 40% 10% 10%;
  }
`

// Create a styled component that tracks if it was ever opened
const StyledModalDiv = styled.div`
  position: fixed;
  width: 315px;
  max-width: calc(100vw - 32px);
  background: rgba(255, 255, 255, 1);
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  font-family: ${p => p.theme.fontFamilyBase};
  font-weight: 600;
  /* модалы на сайте имеют z-index 1000, нельзя поднимать кнопку связаться с нами выше них, то есть её z-index должен быть всгеда ниже 1000 */
  z-index: 777;
  transform-origin: bottom right;
  will-change: transform, opacity, border-radius;

  /* Base visibility properties - only hide when not in closing animation */
  visibility: ${p => (p.open || p.isClosing) ? 'visible' : 'hidden'};
  pointer-events: ${p => p.open ? 'auto' : 'none'};

  /* Animation fill mode */
  animation-fill-mode: forwards;

  /* Apply animations */
  ${p => p.open && !p.isClosing && css`
    animation: ${dropletIn} 0.75s cubic-bezier(0.34, 1.2, 0.64, 1) forwards;
  `}

  /* Only apply the exit animation when closing */
  ${p => p.isClosing && css`
    animation: ${dropletOut} 0.75s cubic-bezier(0.34, 1.2, 0.64, 1) forwards;
  `}

  ${p => p.isMobile && css`
    transform-origin: bottom right;
  `}

  ${p => p.css}
`

const _ContactUs_Modal = forwardRef(({ open, ...rest }, ref) => {
  // Track if the modal has been opened at least once
  const [wasOpened, setWasOpened] = React.useState(false);
  // Track if the modal is in the process of closing
  const [isClosing, setIsClosing] = React.useState(false);
  // Store previous open state to detect changes
  const prevOpenRef = React.useRef(open);

  React.useEffect(() => {
    // If opening for the first time
    if (open && !wasOpened) {
      setWasOpened(true);
    }

    // If closing (was open and now is not)
    if (prevOpenRef.current && !open) {
      setIsClosing(true);

      // After animation ends, set isClosing to false
      const animationTimeout = setTimeout(() => {
        setIsClosing(false);
      }, 750); // Match the animation duration (0.75s)

      return () => clearTimeout(animationTimeout);
    }

    // If opening (was closed and now is open)
    if (!prevOpenRef.current && open) {
      setIsClosing(false);
    }

    // Update previous open state
    prevOpenRef.current = open;
  }, [open, wasOpened]);

  return <StyledModalDiv open={open} wasOpened={wasOpened} isClosing={isClosing} ref={ref} {...rest} />;
})

_ContactUs_Modal.propTypes = {}
_ContactUs_Modal.className = StyledModalDiv

export default _ContactUs_Modal
